import React, { Component } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import PropTypes from 'prop-types';
import { push } from 'modules/history';
import { Colors } from 'modules/theme';
import Icon from 'components/Icon';
import Loader from 'components/Loader';
import HealthCheckAttemptCard from './HealthCheckAttemptCard';
import AttemptTimeline from './AttemptTimeline';
import {
  DEVICE_TITLES,
  RO_CHECK_ITEMS,
  CHECK_ITEMS_BY_DEVICE,
  EXECUTION_SOURCE_LABELS,
  formatTriggeredBy,
} from './constants';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  background-color: ${Colors.white};
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 30px;
  margin-left: -250px;
  width: calc(100% + 250px);
  padding-left: 280px;
  border-bottom: 1px solid ${Colors.gray2};
  background-color: ${Colors.white};
  min-height: 56px;
  box-sizing: border-box;
  gap: 10px 16px;

  @media (max-width: 700px) {
    margin-left: -60px;
    width: calc(100% + 60px);
    padding-left: 90px;
    padding-top: 10px;
    padding-bottom: 10px;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 24px 30px;
  padding-left: 280px;
  margin-left: -250px;
  width: calc(100% + 250px);
  background-color: #fafbfc;
  box-sizing: border-box;

  @media (max-width: 700px) {
    margin-left: -60px;
    width: calc(100% + 60px);
    padding-left: 90px;
  }
`;

const BackButton = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  max-width: 100%;
  color: #1788c5;
  font-size: 16px;
  font-family: Gotham, 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 350;
  line-height: 24px;
  cursor: pointer;
`;

const HeaderTitle = styled.div`
  color: #1788c5;
  font-size: 16px;
  font-weight: 350;
  line-height: 24px;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const HeaderDivider = styled.div`
  width: 1px;
  height: 20px;
  background-color: #dddddd;
  flex: 0 0 auto;
`;

const HeaderLocationIdentificator = styled.div`
  color: ${Colors.black};
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const SummaryCard = styled.div`
  background: ${Colors.white};
  border: 1px solid #e6e8eb;
  border-radius: 10px;
  padding: 18px 22px;
  margin-bottom: 20px;
`;

const SummaryTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 4px 11px;
  border-radius: 6px;
  background: ${props => props.$bg};
  color: ${props => props.$color};
  font-size: 13px;
  font-family: Gotham, sans-serif;
  font-weight: 400;
  line-height: 18px;
`;

const StatusDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 9999px;
  background: ${props => props.$color};
`;

const HistoryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  background: none;
  color: #6b6e72;
  border: none;
  font-size: 13px;
  font-family: Gotham, sans-serif;
  font-weight: 350;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: #1a1a1a;
  }
`;

const RerunButton = styled(HistoryButton)`
  color: #1788c5;
  font-weight: 400;

  &:disabled {
    color: #9a9da0;
    cursor: not-allowed;
  }

  &:hover {
    color: ${props => (props.disabled ? '#9a9da0' : '#0f6f9f')};
  }
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 14px;
  color: #6b6e72;
  font-size: 12.5px;
  font-family: Gotham, sans-serif;
  font-weight: 325;
  line-height: 18px;
`;

const MetaItem = styled.span`
  overflow-wrap: anywhere;

  b {
    color: #9a9da0;
    font-weight: 350;
  }
`;

const MetaSep = styled.span`
  margin: 0 10px;
  color: #d3d5d7;
`;

const Breakdown = styled.div`
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #f0f1f3;
`;

const BreakdownLabels = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
`;

const BreakdownTitle = styled.span`
  color: #3f4145;
  font-size: 13px;
  font-family: Gotham, sans-serif;
  font-weight: 400;
`;

const BreakdownCounts = styled.span`
  display: inline-flex;
  gap: 14px;
  color: #8a8d90;
  font-size: 12px;
  font-family: Gotham, sans-serif;
  font-weight: 325;
`;

const Bar = styled.div`
  display: flex;
  width: 100%;
  height: 5px;
  border-radius: 999px;
  overflow: hidden;
  background: #edeef0;
`;

const BarSegment = styled.div`
  height: 100%;
  width: ${props => props.$pct}%;
  background: ${props => props.$color};
  transition: width 0.2s ease;
`;

const Body = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const RailColumn = styled.div`
  flex: 0 0 248px;
  width: 248px;
  position: sticky;
  top: 24px;

  @media (max-width: 900px) {
    flex: 1 1 auto;
    width: 100%;
    position: static;
  }
`;

const OlderReports = styled.div`
  border: 1px solid #e6e8eb;
  border-radius: 10px;
  background: #ffffff;
  overflow: hidden;
  margin-top: 14px;

  @media (max-width: 900px) {
    width: 100%;
    margin-top: 0;
  }
`;

const OlderReportsTitle = styled.div`
  color: #8a8d90;
  font-size: 11px;
  font-family: Gotham, sans-serif;
  font-weight: 400;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 12px 14px 8px;
`;

const OlderReportItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 10px 14px;
  border: none;
  border-top: 1px solid #f0f1f3;
  border-left: 2px solid ${props => (props.$selected ? '#1788C5' : 'transparent')};
  background: ${props => (props.$selected ? '#f6f8fa' : 'transparent')};
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover {
    background: ${props => (props.$selected ? '#f6f8fa' : '#fafbfc')};
  }
`;

const OlderReportBody = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 1px;
`;

const OlderReportLabel = styled.span`
  color: #3f4145;
  font-size: 13px;
  font-family: Gotham, sans-serif;
  font-weight: ${props => (props.$selected ? 400 : 350)};
  line-height: 18px;
  white-space: nowrap;
`;

const OlderReportMeta = styled.span`
  color: #8a8d90;
  font-size: 11.5px;
  font-family: Gotham, sans-serif;
  font-weight: 325;
  line-height: 16px;
  white-space: nowrap;
`;

const OlderReportsEmpty = styled.div`
  color: #8a8d90;
  font-size: 12px;
  font-family: Gotham, sans-serif;
  font-weight: 325;
  line-height: 16px;
  padding: 10px 14px 12px;
  border-top: 1px solid #f0f1f3;
`;

const ChecksColumn = styled.div`
  flex: 1 1 0;
  min-width: 0;
`;

const LoaderWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px;
`;

const InlineLoaderWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px;
  border: 1px solid #e6e8eb;
  border-radius: 10px;
  background: ${Colors.white};
`;

const ErrorMessage = styled.div`
  padding: 24px;
  color: #c80000;
  font-size: 14px;
`;

const EmptyState = styled.div`
  padding: 24px;
  color: #808285;
  font-size: 14px;
`;

const STATUS_BADGE = {
  Pass: { bg: '#EAF6EC', color: '#1E7D2E', dot: '#1E9331' },
  Failed: { bg: '#FBEAE8', color: '#B03029', dot: '#C7362F' },
  Skipped: { bg: '#EEF0F2', color: '#6B6E72', dot: '#9A9DA0' },
};

/**
 * Maps API response to attempts for the UI.
 * La API devuelve report.attempts[] con { attempt_number, overall_status, started_at, completed_at, checks[] }.
 */
function mapReportToAttempts(report) {
  const apiAttempts = report?.attempts;
  if (!apiAttempts?.length) return [];

  const reportTriggeredBy = formatTriggeredBy(report?.triggered_by);

  return apiAttempts.map(attempt => {
    const checksMap = (attempt.checks || []).reduce((acc, c) => {
      if (c.check_key) acc[c.check_key] = c.status === 'passed';
      return acc;
    }, {});
    const state =
      attempt.overall_status === 'failed'
        ? 'Failed'
        : attempt.overall_status === 'passed'
        ? 'Pass'
        : 'Skipped';
    const timestamp =
      attempt.completed_at || attempt.started_at
        ? moment(attempt.completed_at || attempt.started_at).format('MM/DD/YYYY HH:mm:ss')
        : '—';

    return {
      id: attempt.attempt_number ?? attempt.checks?.[0]?.health_check_id,
      state,
      mode: EXECUTION_SOURCE_LABELS[report?.execution_source] || '—',
      triggeredBy: reportTriggeredBy,
      timestamp,
      checks: checksMap,
      checksList: attempt.checks || [],
    };
  });
}
/**
 * Check items for the table: use CHECK_ITEMS_BY_DEVICE if present, otherwise build from first attempt keys.
 */
function getCheckItemsForReport(report) {
  const deviceType = report?.device_type || 'reverse-osmosis';
  const list = CHECK_ITEMS_BY_DEVICE[deviceType] || RO_CHECK_ITEMS;
  if (list.length) return list;
  const firstAttempt = report?.attempts?.[0];
  const checks = firstAttempt?.checks || [];
  const keys = [...new Set(checks.map(c => c.check_key).filter(Boolean))];
  return keys.map(key => ({ key, label: key.replace(/-/g, ' '), hasInfo: false }));
}

function countAttemptChecks(attempt) {
  const list = attempt?.checksList || [];
  const passed = list.filter(c => c.status === 'passed').length;
  return { passed, failed: list.length - passed, total: list.length };
}

function getReportId(report) {
  return report?.report_id ?? report?.id;
}

function getReportState(report) {
  const status = report?.status ?? report?.overall_status;
  if (status === 'failed') return 'Failed';
  if (status === 'passed') return 'Pass';
  return 'Skipped';
}

function getReportDate(report) {
  const date = report?.completed_at || report?.started_at || report?.created_at;
  return date ? moment(date).format('MM/DD/YYYY HH:mm:ss') : '—';
}

export default class HealthCheckDetailView extends Component {
  state = {
    selectedAttemptId: null,
    /** When report_id changes, select first attempt once (avoids setState in mount/update). */
    syncedReportKey: null,
    pendingReportKey: null,
    pendingAttemptId: null,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const { report } = nextProps;
    const reportKey =
      report?.report_id != null && report.report_id !== '' ? String(report.report_id) : null;
    if (!reportKey || prevState.syncedReportKey === reportKey) {
      return null;
    }
    const attempts = mapReportToAttempts(report);
    const pendingAttemptId =
      prevState.pendingReportKey === reportKey ? prevState.pendingAttemptId : null;
    return {
      syncedReportKey: reportKey,
      selectedAttemptId:
        pendingAttemptId != null ? pendingAttemptId : attempts.length > 0 ? attempts[0].id : null,
      pendingReportKey: null,
      pendingAttemptId: null,
    };
  }

  static propTypes = {
    error: PropTypes.string,
    loading: PropTypes.bool.isRequired,
    location: PropTypes.object,
    olderReports: PropTypes.array,
    olderReportsLoading: PropTypes.bool,
    railReport: PropTypes.object,
    report: PropTypes.object,
    reportId: PropTypes.string,
    selectedReportLoading: PropTypes.bool,
    showAlert: PropTypes.func,
    startHealthCheck: PropTypes.func,
    startHealthCheckLoading: PropTypes.bool,
  };

  static defaultProps = {
    olderReports: [],
    olderReportsLoading: false,
    selectedReportLoading: false,
    startHealthCheckLoading: false,
  };

  goBack = () => {
    push('/health-checks');
  };

  goToLocationList = () => {
    const { report } = this.props;
    if (report?.location_id) {
      push(`/health-checks?location_id=${encodeURIComponent(report.location_id)}`);
    }
  };

  selectAttempt = attemptId => {
    const { railReport, report } = this.props;
    const railReportId = getReportId(railReport);
    const reportId = getReportId(report);

    if (
      railReportId != null &&
      railReportId !== '' &&
      reportId != null &&
      String(railReportId) !== String(reportId)
    ) {
      this.setState({
        pendingReportKey: String(railReportId),
        pendingAttemptId: attemptId,
      });
      push(`/health-checks/detail/${railReportId}`);
      return;
    }

    this.setState({ selectedAttemptId: attemptId });
  };

  rerunHealthCheck = () => {
    const { report, showAlert, startHealthCheck, startHealthCheckLoading } = this.props;

    if (startHealthCheckLoading) {
      return;
    }

    if (!report?.location_id || !report?.device_type || !report?.report_id) {
      if (showAlert) {
        showAlert('Unable to rerun health check for this report', {
          variant: 'warning',
          timeout: 9,
        });
      }
      return;
    }

    startHealthCheck({
      location_id: report.location_id,
      device_type: report.device_type,
      report_id: report.report_id,
    });
  };

  goToReport = reportId => {
    if (reportId == null || reportId === '') return;
    push(`/health-checks/detail/${reportId}`);
  };

  renderOlderReports(reports, loading, selectedReportId) {
    return (
      <OlderReports>
        <OlderReportsTitle>Older health checks</OlderReportsTitle>
        {loading ? (
          <OlderReportsEmpty>Loading...</OlderReportsEmpty>
        ) : reports.length ? (
          reports.map(report => {
            const reportId = getReportId(report);
            const selected = String(reportId) === String(selectedReportId);
            const state = getReportState(report);
            const badge = STATUS_BADGE[state] || STATUS_BADGE.Skipped;
            const attempts =
              report.attempts != null && report.attempts !== ''
                ? Number(report.attempts)
                : Math.max(0, Number(report.retry_count || 0)) + 1;

            return (
              <OlderReportItem
                key={reportId}
                type="button"
                $selected={selected}
                onClick={() => this.goToReport(reportId)}
              >
                <StatusDot $color={badge.dot} />
                <OlderReportBody>
                  <OlderReportLabel $selected={selected}>{state}</OlderReportLabel>
                  <OlderReportMeta>
                    {attempts > 1 ? `${attempts} attempts · ` : ''}
                    {getReportDate(report)}
                  </OlderReportMeta>
                </OlderReportBody>
              </OlderReportItem>
            );
          })
        ) : (
          <OlderReportsEmpty>No older health checks</OlderReportsEmpty>
        )}
      </OlderReports>
    );
  }

  renderHeader(title, locationIdentificator) {
    return (
      <PageHeader>
        <BackButton onClick={this.goBack}>
          <Icon name="chevron-left" fontSize={16} color="#1788C5" />
          <HeaderTitle>{title}</HeaderTitle>
        </BackButton>
        {locationIdentificator && (
          <>
            <HeaderDivider />
            <HeaderLocationIdentificator>{locationIdentificator}</HeaderLocationIdentificator>
          </>
        )}
      </PageHeader>
    );
  }

  renderShell(title, children) {
    return (
      <Container>
        {this.renderHeader(title)}
        <ContentArea>{children}</ContentArea>
      </Container>
    );
  }

  render() {
    const {
      report,
      loading,
      error,
      location,
      olderReports,
      olderReportsLoading,
      reportId: reportIdFromRoute,
      railReport,
      selectedReportLoading,
      startHealthCheckLoading,
    } = this.props;
    const { selectedAttemptId } = this.state;

    const reportId = reportIdFromRoute ?? location?.state?.report_id;
    const railOrSelectedReport = railReport || report;
    const locationName =
      report?.location_name ||
      railOrSelectedReport?.location_name ||
      location?.state?.locationName ||
      '—';
    const deviceType =
      report?.device_type ||
      railOrSelectedReport?.device_type ||
      location?.state?.deviceType ||
      'reverse-osmosis';

    const title =
      DEVICE_TITLES[deviceType] || DEVICE_TITLES['reverse-osmosis'] || 'Health Check Details';

    if (!reportId) {
      return this.renderShell(
        title,
        <EmptyState>
          No report selected. Go back to the list and choose &quot;View details&quot;.
        </EmptyState>,
      );
    }

    if (loading) {
      return this.renderShell(
        title,
        <LoaderWrap>
          <Loader size={40} />
        </LoaderWrap>,
      );
    }

    if (error) {
      return this.renderShell(title, <ErrorMessage>{error}</ErrorMessage>);
    }

    if (!report) {
      return this.renderShell(
        title,
        <EmptyState>
          This report could not be loaded. Go back to the list and try again.
        </EmptyState>,
      );
    }

    const checkItems = getCheckItemsForReport(report);
    const attempts = mapReportToAttempts(report);
    const railAttempts = mapReportToAttempts(railOrSelectedReport);
    const selectedReportId = getReportId(report);
    const railReportId = getReportId(railOrSelectedReport);
    const viewingRailReport =
      selectedReportId != null &&
      railReportId != null &&
      String(selectedReportId) === String(railReportId);
    const showInlineLoader =
      selectedReportLoading && reportId != null && String(selectedReportId) !== String(reportId);
    const olderReportsForLocation = (olderReports || []).filter(
      item =>
        String(getReportId(item)) !== String(railReportId) &&
        (!railOrSelectedReport?.device_type ||
          item.device_type === railOrSelectedReport.device_type),
    );

    if (!attempts.length) {
      return this.renderShell(
        title,
        <EmptyState>There are no attempts recorded for this report.</EmptyState>,
      );
    }

    const selectedAttempt = attempts.find(a => a.id === selectedAttemptId) || attempts[0];
    const badge = STATUS_BADGE[selectedAttempt.state] || STATUS_BADGE.Failed;
    const { passed, failed, total } = countAttemptChecks(selectedAttempt);
    const passPct = total > 0 ? (passed / total) * 100 : 0;
    const failPct = total > 0 ? (failed / total) * 100 : 0;

    const metaItems = [
      locationName && locationName !== '—' ? <MetaItem key="loc">{locationName}</MetaItem> : null,
      <MetaItem key="mode">
        <b>Mode</b> {selectedAttempt.mode}
      </MetaItem>,
      report.triggered_by ? (
        <MetaItem key="trig">
          <b>Triggered by</b> {formatTriggeredBy(report.triggered_by)}
        </MetaItem>
      ) : null,
      <MetaItem key="run">{selectedAttempt.timestamp}</MetaItem>,
    ].filter(Boolean);
    const showRail =
      railAttempts.length > 1 || olderReportsLoading || olderReportsForLocation.length > 0;

    return (
      <Container>
        {this.renderHeader(title, locationName !== '—' ? locationName : null)}
        <ContentArea>
          <SummaryCard>
            <SummaryTop>
              <StatusBadge $bg={badge.bg} $color={badge.color}>
                <StatusDot $color={badge.dot} />
                {selectedAttempt.state}
              </StatusBadge>
              {report.location_id && report.report_id && (
                <RerunButton
                  type="button"
                  onClick={this.rerunHealthCheck}
                  disabled={startHealthCheckLoading}
                >
                  <Icon
                    name="heartbeat"
                    fontSize={12}
                    color={startHealthCheckLoading ? '#9a9da0' : '#1788c5'}
                  />
                  {startHealthCheckLoading ? 'Running Health Check' : 'Rerun Health Check'}
                </RerunButton>
              )}
            </SummaryTop>

            <MetaRow>
              {metaItems.map((item, i) => (
                <React.Fragment key={item.key}>
                  {i > 0 && <MetaSep>·</MetaSep>}
                  {item}
                </React.Fragment>
              ))}
            </MetaRow>

            {total > 0 && (
              <Breakdown>
                <BreakdownLabels>
                  <BreakdownTitle>
                    {passed} of {total} checks passed
                  </BreakdownTitle>
                  <BreakdownCounts>
                    <span>{passed} passed</span>
                    <span>{failed} failed</span>
                  </BreakdownCounts>
                </BreakdownLabels>
                <Bar>
                  <BarSegment $pct={passPct} $color="#1E9331" />
                  <BarSegment $pct={failPct} $color="#C7362F" />
                </Bar>
              </Breakdown>
            )}
          </SummaryCard>

          <Body>
            {showRail && (
              <RailColumn>
                {railAttempts.length > 1 && (
                  <AttemptTimeline
                    attempts={railAttempts}
                    selectedId={viewingRailReport ? selectedAttempt.id : null}
                    onSelect={this.selectAttempt}
                  />
                )}
                {this.renderOlderReports(
                  olderReportsForLocation,
                  olderReportsLoading,
                  selectedReportId,
                )}
              </RailColumn>
            )}
            <ChecksColumn>
              {showInlineLoader ? (
                <InlineLoaderWrap>
                  <Loader size={32} />
                </InlineLoaderWrap>
              ) : (
                <HealthCheckAttemptCard
                  attempt={selectedAttempt}
                  checkItems={checkItems}
                  defaultCheckValue={false}
                />
              )}
            </ChecksColumn>
          </Body>
        </ContentArea>
      </Container>
    );
  }
}
